import * as React from 'react'

import { Info } from 'lucide-react'
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Tooltip, TooltipContent, TooltipTrigger } from '../shadcn-ui/tooltip'
import { formatHours } from './format'

export interface IUtilizationChartDay {
  key: number
  shortLabel: string
  fullLabel: string
  hours: number
  overtimeHours: number
  isWorkingDay: boolean
  isToday: boolean
  hasOvertime: boolean
}

const UtilizationTooltip: React.FC<{
  active?: boolean
  payload?: { payload: IUtilizationChartDay }[]
}> = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  const day = payload[0].payload
  const { overtimeHours } = day

  return (
    <div className="tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-popover tw:px-2.5 tw:py-1.5 tw:text-popover-foreground tw:shadow-md">
      <div className="tw:text-body-sm tw:text-muted-foreground">
        {day.fullLabel}
        {day.isToday && ' · today'}
        {!day.isWorkingDay && ' · non-working'}
      </div>
      <div className="tw:text-title-sm tw:text-foreground tw:tabular-nums">
        {formatHours(day.hours)}
      </div>
      {overtimeHours > 0 && (
        <div className="tw:text-body-sm tw:text-muted-foreground tw:tabular-nums">
          +{formatHours(overtimeHours)} overtime
        </div>
      )}
    </div>
  )
}

interface IUtilizationChartProps {
  days: IUtilizationChartDay[]
  shiftHours: number
  totalHours: number
  percentDaysUsed: number | null
  utilizationRate: number | null
}

const formatPercent = (value: number | null, decimals = 0): string =>
  value === null || value === undefined ? '—' : `${value.toFixed(decimals)}%`

const StatLabel: React.FC<{
  label: string
  hint: string
}> = ({ label, hint }) => (
  <span className="tw:inline-flex tw:items-center tw:gap-1">
    {label}
    <Tooltip>
      <TooltipTrigger
        type="button"
        aria-label={`What does ${label} mean?`}
        className="tw:inline-flex tw:items-center tw:justify-center tw:cursor-default tw:text-muted-foreground tw:hover:text-foreground tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-1 tw:rounded-sm"
      >
        <Info className="tw:size-3.5" />
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  </span>
)

const UtilizationChart: React.FC<IUtilizationChartProps> = React.memo(
  ({ days, shiftHours, totalHours, percentDaysUsed, utilizationRate }) => {
    const maxHours = Math.max(...days.map(d => d.hours), shiftHours)
    const yMax = Math.max(Math.ceil(maxHours * 1.1), Math.ceil(shiftHours) || 1)
    const hasShiftLine = shiftHours > 0

    return (
      <div className="tw:flex tw:flex-col tw:gap-3 tw:px-1 tw:py-1">
        <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-2">
          <div className="tw:flex tw:flex-col tw:gap-0.5">
            <div className="tw:text-body-sm tw:text-muted-foreground">
              <StatLabel
                label="Engine on"
                hint="Total hours the equipment is moving or idle. Last 7 days."
              />
            </div>
            <div className="tw:text-headline-sm tw:text-foreground tw:tabular-nums">
              {formatHours(totalHours)}
            </div>
          </div>
          <div className="tw:flex tw:items-baseline tw:gap-3 tw:text-body-sm tw:text-muted-foreground tw:tabular-nums">
            <StatLabel
              label={`Days used ${formatPercent(percentDaysUsed)}`}
              hint="Percentage of working days in the window with engine-on time."
            />
            <StatLabel
              label={`Utilization ${formatPercent(utilizationRate, 1)}`}
              hint="Engine-on hours over available shift hours. Can exceed 100% with off-hours use."
            />
          </div>
        </div>
        <div className="tw:h-28 tw:w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={days}
              margin={{ top: 12, right: 8, left: 0, bottom: 0 }}
              barCategoryGap="22%"
            >
              <YAxis hide domain={[0, yMax]} />
              <XAxis
                dataKey="shortLabel"
                tickLine={false}
                axisLine={false}
                tick={({ x, y, payload, index }) => {
                  const day = days[index]
                  return (
                    <text
                      x={x}
                      y={y + 10}
                      textAnchor="middle"
                      className="tw:tabular-nums"
                      style={{
                        fontSize: 11,
                        fill: day?.isToday
                          ? 'var(--foreground)'
                          : 'var(--muted-foreground)',
                        fontWeight: day?.isToday ? 600 : 400,
                      }}
                    >
                      {payload.value}
                    </text>
                  )
                }}
                interval={0}
                height={20}
              />
              {hasShiftLine && (
                <ReferenceLine
                  y={shiftHours}
                  stroke="var(--border)"
                  strokeDasharray="3 4"
                  strokeWidth={1}
                />
              )}
              <RechartsTooltip
                cursor={{ fill: 'var(--muted)', opacity: 0.4 }}
                content={<UtilizationTooltip />}
              />
              <Bar
                dataKey="hours"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                {days.map(day => (
                  <Cell
                    key={day.key}
                    fill={
                      day.hasOvertime
                        ? 'var(--foreground)'
                        : day.isWorkingDay
                          ? 'var(--primary)'
                          : 'var(--muted-foreground)'
                    }
                    fillOpacity={
                      day.isToday ? 0.45 : day.isWorkingDay ? 1 : 0.55
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    )
  },
)
UtilizationChart.displayName = 'UtilizationChart'

export default UtilizationChart
